/**
 * Which company's books are currently open.
 *
 * Accounting is entered one company at a time: you pick a company on the way
 * in, and everything you then see and post belongs to it. Leaving accounting,
 * by any route, closes it again, so coming back always asks which company to
 * open — posting into the wrong company is the one mistake here that is
 * expensive to undo, and a company left quietly open is how that happens.
 *
 * Kept in sessionStorage, which makes the choice belong to this tab: a refresh
 * or a link within accounting stays in the same books, while a second tab is a
 * clean slate and cannot be dragged out of its own company by what the first
 * one does. It is browser-side only — the server is told which company each
 * request is for and refuses anything it does not recognise.
 */

const KEY = "bericot@acc_company";
const EVENT = "acc-company-changed";

/** The open company, or null if none has been chosen. */
export const getCompany = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const company = JSON.parse(raw);
    return company && company.id ? company : null;
  } catch {
    // A corrupted or unreadable entry should send the user to the picker, not
    // break the page.
    return null;
  }
};

export const getCompanyId = () => getCompany()?.id ?? null;

export const setCompany = (company) => {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({
        id: company.id,
        name: company.name,
        currency: company.currency,
        industry: company.industry,
      })
    );
  } catch {
    // Storage can be unavailable (private windows, blocked site data). The
    // choice then lasts only for this page, which is still usable.
  }
  window.dispatchEvent(new Event(EVENT));
};

export const clearCompany = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
  window.dispatchEvent(new Event(EVENT));
};

/**
 * Subscribe to the open company changing. Returns the unsubscribe function, so
 * a component can hand it straight back from useEffect.
 */
export const onCompanyChange = (handler) => {
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
};

export default { getCompany, getCompanyId, setCompany, clearCompany, onCompanyChange };
